// Build render-only indices from persistent boundary IDs. No store name matching.
export function buildBoundaryReference(boundaries) {
  const all = new Map();
  const countries = new Set();
  for (const row of boundaries) {
    if (all.has(row.area_id)) throw new Error('Duplicate boundary ID.');
    if (![1, 2, 3, 4].includes(Number(row.geo_tier))) throw new Error('Unsupported boundary tier.');
    all.set(row.area_id, row);
    countries.add(row.iso3);
  }
  if (countries.size !== 1) throw new Error('Boundaries must belong to one country.');
  const geo = { zones: [], states: [], lgas: [] };
  const locations = new Map();
  const levels = {};
  for (const tier of [1, 2, 3, 4]) {
    for (const row of boundaries.filter((area) => Number(area.geo_tier) === tier)) {
      if (tier === 1) { locations.set(row.area_id, [-1, -1, -1, 1]); continue; }
      const parent = all.get(row.parent_area_id);
      const placement = locations.get(row.parent_area_id);
      if (!parent || !placement || Number(parent.geo_tier) !== tier - 1) throw new Error('Incomplete boundary hierarchy.');
      const geometry = typeof row.geometry === 'string' ? JSON.parse(row.geometry) : row.geometry;
      if (!geometry || !['Polygon', 'MultiPolygon'].includes(geometry.type)) throw new Error('Invalid boundary geometry.');
      const key = { 2: 'zones', 3: 'states', 4: 'lgas' }[tier];
      const index = geo[key].length;
      const zi = tier === 2 ? index : placement[2];
      const si = tier === 3 ? index : placement[1];
      geo[key].push({ id: index, area_id: row.area_id, name: row.area_name, g: geometry, zi, si });
      locations.set(row.area_id, [tier === 4 ? index : -1, si, zi, tier]);
      levels[tier] = row.level_name;
    }
  }
  geo.attribution = boundaries.find((row) => row.attribution)?.attribution || '';
  return { geo, locations, levels };
}

const storeKey = (level, storeName) => JSON.stringify([Number(level), String(storeName ?? '')]);

export function buildLiveMapData(rows, reference) {
  const reportedMonths = [...new Set(rows.map((row) => row.report_month))].sort();
  const months = [];
  if (reportedMonths.length) {
    let [year, month] = reportedMonths[0].split('-').map(Number);
    const last = reportedMonths[reportedMonths.length - 1];
    while (`${year}-${String(month).padStart(2, '0')}` <= last) {
      months.push(`${year}-${String(month).padStart(2, '0')}`);
      if (++month > 12) { month = 1; year++; }
    }
  }
  const antigens = [...new Set(rows.map((row) => String(row.antigen_name ?? '')))].sort();
  const monthIndex = new Map(months.map((month, index) => [month, index]));
  const antigenIndex = new Map(antigens.map((antigen, index) => [antigen, index]));
  const stores = [];
  const storeIndex = new Map();
  const newStores = new Set();
  const recs = rows.map((row) => {
    const key = storeKey(row.sc_level_no, row.store_name);
    let area = reference.locations.get(row.geo_area_id);
    if (!area) {
      newStores.add(key);
      area = [-1, -1, -1, 0];
    }
    if (!storeIndex.has(key)) { storeIndex.set(key, stores.length); stores.push(String(row.store_name ?? '')); }
    const count = Number(row.record_count);
    if (!Number.isSafeInteger(count) || count <= 0 || !monthIndex.has(row.report_month)) throw new Error('Invalid stockout aggregation returned by the database.');
    return [...area, storeIndex.get(key), antigenIndex.get(String(row.antigen_name ?? '')), monthIndex.get(row.report_month), count];
  });
  return {
    meta: {
      levels: reference.levels,
      generatedAt: new Date().toISOString(),
      newStores: newStores.size
    },
    months, antigens, stores, recs
  };
}
